import {
  collection,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

// Request browser notification permission
export const requestNotificationPermission = async () => {
  if (!("Notification" in window)) {
    console.log("This browser does not support notifications");
    return false;
  }

  if (Notification.permission === "granted") {
    return true;
  }

  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }

  return false;
};

// Send browser notification
export const sendBrowserNotification = (title, options = {}) => {
  if (!("Notification" in window)) {
    return;
  }

  if (Notification.permission === "granted") {
    const notification = new Notification(title, {
      icon: "/logo192.png",
      badge: "/logo192.png",
      ...options,
    });

    // Auto-close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);

    // Handle click
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  }
};

// Create in-app notification in Firestore
export const createNotification = async (
  userId,
  notificationData,
  customId
) => {
  try {
    const notifRef = customId
      ? doc(db, "notifications", customId)
      : doc(collection(db, "notifications"));
    await setDoc(notifRef, {
      userId,
      ...notificationData,
      createdAt: serverTimestamp(),
      read: false,
    });
    return notifRef.id;
  } catch (error) {
    console.error("Error creating notification:", error);
    throw error;
  }
};

// Get user notification settings
const getUserNotificationSettings = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      const data = userDoc.data();
      return (
        data.notificationSettings || {
          appointmentStarted: true,
          appointmentEnded: true,
          appointmentCompleted: true,
          appointmentCancelled: true,
          appointmentRescheduled: true,
          appointmentIncomplete: true,
          paymentReceived: true,
          paymentPending: true,
          browserNotifications: true,
        }
      );
    }
  } catch (error) {
    console.error("Error fetching notification settings:", error);
  }
  // Default to all enabled if settings not found
  return {
    appointmentStarted: true,
    appointmentEnded: true,
    appointmentCompleted: true,
    appointmentCancelled: true,
    appointmentRescheduled: true,
    appointmentIncomplete: true,
    paymentReceived: true,
    paymentPending: true,
    browserNotifications: true,
  };
};

// Map notification type to setting key
const getSettingKey = (type) => {
  const mapping = {
    started: "appointmentStarted",
    appointment_ended: "appointmentEnded",
    appointment_completed: "appointmentCompleted",
    appointment_cancelled: "appointmentCancelled",
    appointment_rescheduled: "appointmentRescheduled",
    appointment_incomplete: "appointmentIncomplete",
    payment_received: "paymentReceived",
    payment_pending: "paymentPending",
  };
  return mapping[type] || null;
};

// Create notification and send browser notification
export const notify = async (
  userId,
  notificationData,
  forceBrowser = false
) => {
  try {
    // Get user notification settings
    const settings = await getUserNotificationSettings(userId);
    const settingKey = getSettingKey(notificationData.type);

    // Check if this notification type is enabled
    if (settingKey && settings[settingKey] === false) {
      // User has disabled this notification type
      return;
    }

    // Create in-app notification
    await createNotification(userId, notificationData, notificationData?.id);

    // Send browser notification if enabled
    const shouldSendBrowser =
      forceBrowser || settings.browserNotifications !== false;
    if (shouldSendBrowser) {
      const hasPermission = await requestNotificationPermission();
      if (hasPermission) {
        sendBrowserNotification(
          notificationData.title || notificationData.message,
          {
            body: notificationData.message || notificationData.title,
            tag: notificationData.appointmentId || notificationData.id,
          }
        );
      }
    }
  } catch (error) {
    console.error("Error sending notification:", error);
  }
};

// Schedule an appointment start notification based on user's notification frequency setting
export const scheduleAppointmentStartNotification = async (
  userId,
  appointmentData
) => {
  try {
    // Respect manual suppression set on appointment (if user dismissed earlier)
    if (appointmentData?.suppressStartedNotification) return;
    const settings = await getUserNotificationSettings(userId);

    // Check if appointment started notifications are enabled
    if (settings.appointmentStarted === false) {
      return;
    }

    // Get notification frequency in minutes (default to 15 minutes)
    const frequencyMinutes =
      settings.appointmentStartNotificationFrequency || 15;

    // Get appointment start time
    const appointmentStart = appointmentData.start?.toDate
      ? appointmentData.start.toDate()
      : new Date(appointmentData.start);

    // Calculate notification time (frequency minutes before appointment start)
    const notificationTime = new Date(
      appointmentStart.getTime() - frequencyMinutes * 60 * 1000
    );

    // Calculate delay from now
    const now = new Date();
    const delay = notificationTime.getTime() - now.getTime();

    // Only schedule if notification time is in the future
    if (delay > 0) {
      // Schedule the notification
      setTimeout(async () => {
        try {
          // Avoid recreating a notification if one already exists for this
          // appointment and type (e.g., user deleted it while timer was pending).
          const existing = await getDocs(
            query(
              collection(db, "notifications"),
              where("userId", "==", userId),
              where("appointmentId", "==", appointmentData.id),
              where("type", "==", "started")
            )
          );

          if (!existing.empty) return;

          await notify(userId, {
            type: "started",
            appointmentId: appointmentData.id,
            patientName: appointmentData.patientName || "Patient",
            title: `Appointment Starting Soon`,
            message: `Your appointment with ${
              appointmentData.patientName || "patient"
            } starts in ${frequencyMinutes} minutes.`,
            startTime: appointmentData.start,
          });
        } catch (error) {
          console.error("Error sending scheduled notification:", error);
        }
      }, delay);
    } else if (delay > -3600000) {
      // If notification time is within 1 hour in the past, send immediately
      // Check DB first to avoid creating duplicates
      const existingNow = await getDocs(
        query(
          collection(db, "notifications"),
          where("userId", "==", userId),
          where("appointmentId", "==", appointmentData.id),
          where("type", "==", "started")
        )
      );

      if (existingNow.empty) {
        await notify(userId, {
          type: "started",
          appointmentId: appointmentData.id,
          patientName: appointmentData.patientName || "Patient",
          title: `Appointment Starting Soon`,
          message: `Your appointment with ${
            appointmentData.patientName || "patient"
          } starts in ${frequencyMinutes} minutes.`,
          startTime: appointmentData.start,
        });
      }
    }
  } catch (error) {
    console.error("Error scheduling appointment notification:", error);
  }
};

// Check and send pending appointment notifications (call this when app loads or periodically)
export const checkAndSendPendingNotifications = async (userId) => {
  try {
    const settings = await getUserNotificationSettings(userId);

    // Check if appointment started notifications are enabled
    if (settings.appointmentStarted === false) {
      return;
    }

    const frequencyMinutes =
      settings.appointmentStartNotificationFrequency || 15;

    // Get all future appointments for this user
    const appointmentsSnap = await getDocs(
      query(collection(db, "appointments"), where("userId", "==", userId))
    );

    const now = new Date();
    const appointments = appointmentsSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));

    for (const appointment of appointments) {
      // Skip if user dismissed scheduled started notification for this appointment
      if (appointment?.suppressStartedNotification) continue;
      // Skip cancelled, completed, or past appointments
      if (
        appointment.status === "Cancelled" ||
        appointment.status === "Completed"
      )
        continue;

      const apptStart = appointment.start?.toDate
        ? appointment.start.toDate()
        : new Date(appointment.start);

      if (apptStart <= now) continue;

      // Calculate when notification should be sent
      const notificationTime = new Date(
        apptStart.getTime() - frequencyMinutes * 60 * 1000
      );

      // Check if notification should be sent now
      if (notificationTime <= now && apptStart > now) {
        // Check if notification was already sent for this appointment
        const existingNotif = await getDocs(
          query(
            collection(db, "notifications"),
            where("userId", "==", userId),
            where("appointmentId", "==", appointment.id),
            where("type", "==", "started")
          )
        );

        // Only send if no notification exists for this appointment
        if (existingNotif.empty) {
          await notify(userId, {
            type: "started",
            appointmentId: appointment.id,
            patientName: appointment.patientName || "Patient",
            title: `Appointment Starting Soon`,
            message: `Your appointment with ${
              appointment.patientName || "patient"
            } starts in ${frequencyMinutes} minutes.`,
            startTime: appointment.start,
          });
        }
      }
    }
  } catch (error) {
    console.error("Error checking pending notifications:", error);
  }
};
